import i18next from 'i18next';
import { of } from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';
import { config } from '@config';
import { CrudServiceBase, RxUtils } from '@utils';
import { httpService } from '@services/http';
import { logger } from '@services/logger';
import { notificationService } from '@services/notification';
import { Stock } from './stock.class';

type Entity = Stock;

export class StockService extends CrudServiceBase<Entity> {
	constructor() {
		super(Stock, 'stock');
	}

	private getBulkUrl() {
		return `${config.apiUrl}/stock-bulk`;
	}

	create(entity: Partial<Entity>, images?: File[]) {
		return super.create(entity).pipe(concatMap((result) => this.updateImages(result, images || [])));
	}

	update(id: string, entity: Partial<Entity>, images?: File[]) {
		return super.update(id, entity).pipe(concatMap((result) => this.updateImages(result, images || [])));
	}

	updateImages(entity: Entity, images: File[]) {
		const formData = new FormData();
		images.map((image) => formData.append('images', image, image.name));
		return httpService
			.post<unknown>(this.getUrl(), `${entity.id}/images`, { body: formData, headers: { 'Content-Type': 'multipart/form-data' } })
			.pipe(
				tap((result) => logger.debug(`[${this.constructor.name}.${this.updateImages.name}]`, result)),
				map(() => entity),
				catchError(() => {
					notificationService.sendError(i18next.t('common.files-upload-failed'));
					return of(entity);
				})
			);
	}

	getImages(entity: Entity) {
		return httpService.get<string[]>(this.getUrl(), `${entity.id}/images`).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getImages.name}]`, result)),
			catchError(() => of([] as string[]))
		);
	}

	downloadBulkTemplateFile() {
		return httpService.get<BlobPart>(this.getBulkUrl(), 'template', { responseType: 'blob' }).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.downloadBulkTemplateFile.name}]`, result)),
			concatMap((result) => RxUtils.valueOrThrow(result, new Error('Response is empty')))
		);
	}

	uploadBulkTemplateFile(body: FormData) {
		return httpService
			.post<unknown>(this.getBulkUrl(), 'upload', {
				body,
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			})
			.pipe(tap((result) => logger.debug(`[${this.constructor.name}.${this.uploadBulkTemplateFile.name}]`, result)));
	}
}

export const stockService = new StockService();
